import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback, useEffect, useState } from 'react';
import Input from '../../../form/Input';
import Select from '../../../form/Select';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import DatePickerInput from '../../../form/DatePickerInput';
import { formatDate } from '../../../../util/util';
import { useOrderStore } from '../../../../store/useOrder';
import { useLoginStore } from '../../../../store/useLogin';
import MobileFilterTabs from '../../../filter/MobileFilterTabs';

interface IFilter {
  telRef: React.RefObject<HTMLInputElement>;
  emailRef: React.RefObject<HTMLInputElement>;
  nameRef: React.RefObject<HTMLInputElement>;
  getFilteredData: (params: any) => void;
  // 회사 선택 관련 (Admin/CS용)
  selectedFranchiseId?: string;
  setSelectedFranchiseId?: (id: string) => void;
}

const OrderFixFilter = ({
  telRef,
  emailRef,
  nameRef,
  getFilteredData,
  selectedFranchiseId = '',
  setSelectedFranchiseId,
}: IFilter) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const { role } = useLoginStore();
  const isAdmin = role === 'ROLE_ADMIN' || role === 'ROLE_CS';
  const [pageSize, setPageSize] = useState<string>('20');
  const [startDate, setStartDate] = useState<Date | null>(() => {
    const currentQuery = queryString.parse(location.search);
    if (currentQuery.confirmStartDt) {
      return new Date(currentQuery.confirmStartDt as string);
    }
    const today = new Date();
    return new Date(today.setDate(today.getDate() - 7));
  });
  const [endDate, setEndDate] = useState<Date | null>(() => {
    const currentQuery = queryString.parse(location.search);
    if (currentQuery.confirmEndDt) {
      return new Date(currentQuery.confirmEndDt as string);
    }
    return new Date();
  });

  const [status, setStatus] = useState<string>('');

  // zustand에서 status 리스트 가져오기
  const { contractStatus } = useOrderStore();

  useEffect(() => {
    const currentQuery = queryString.parse(location.search);

    // 쿼리스트링 값에 따라 필드 업데이트
    if (currentQuery.size) {
      setPageSize(currentQuery.size as string);
    } else {
      setPageSize('20'); // 기본값
    }

    if (currentQuery.telNo && telRef.current) {
      telRef.current.value = currentQuery.telNo as string;
    } else if (telRef.current) {
      telRef.current.value = ''; // 초기화
    }

    if (currentQuery.email && emailRef.current) {
      emailRef.current.value = currentQuery.email as string;
    } else if (emailRef.current) {
      emailRef.current.value = ''; // 초기화
    }

    if (currentQuery.custName && nameRef.current) {
      nameRef.current.value = currentQuery.custName as string;
    } else if (nameRef.current) {
      nameRef.current.value = ''; // 초기화
    }

    if (currentQuery.statusList) {
      setStatus(currentQuery.statusList as string);
    } else {
      setStatus(''); // 기본값
    }

    if (currentQuery.confirmStartDt) {
      setStartDate(new Date(currentQuery.confirmStartDt as string));
    } else {
      const today = new Date();
      setStartDate(new Date(today.setDate(today.getDate() - 7)));
    }

    if (currentQuery.confirmEndDt) {
      setEndDate(new Date(currentQuery.confirmEndDt as string));
    } else {
      setEndDate(new Date());
    }
  }, [location.search, telRef, emailRef, nameRef]);

  const updateQueryString = useCallback(
    (updatedFields: Record<string, string | undefined>) => {
      const currentQuery = queryString.parse(location.search);
      const updatedQuery = { ...currentQuery, ...updatedFields };
      const newQueryString = queryString.stringify(updatedQuery);
      navigate(`?${newQueryString}`);
    },
    [location.search, navigate]
  );

  const onChangePageSize = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value;
      setPageSize(value);
      updateQueryString({ size: value, page: '0' });
    },
    [updateQueryString]
  );

  const onChangeContractStatus = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value;
      setStatus(value);
      updateQueryString({ statusList: value, page: '0' });
    },
    [updateQueryString]
  );

  const handleStartDateChange = useCallback(
    (date: Date | null) => {
      setStartDate(date);
      updateQueryString({
        confirmStartDt: date ? formatDate(date)['yyyy-mm-ddTHH:mm:ss'] : undefined,
      });
    },
    [updateQueryString]
  );

  const handleEndDateChange = useCallback(
    (date: Date | null) => {
      setEndDate(date);
      updateQueryString({
        confirmEndDt: date ? formatDate(date)['yyyy-mm-ddTHH:mm:ss'] : undefined,
      });
    },
    [updateQueryString]
  );

  const handleSearch = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const params = {
        size: pageSize,
        telNo: telRef.current?.value || undefined,
        email: emailRef.current?.value || undefined,
        custName: nameRef.current?.value || undefined,
        status: status !== '' ? status : undefined,
        confirmStartDt: startDate
          ? formatDate(startDate)['yyyy-mm-ddTHH:mm:ss']
          : undefined,
        confirmEndDt: endDate
          ? formatDate(endDate)['yyyy-mm-ddTHH:mm:ss']
          : undefined,
      };
      getFilteredData(params);
    },
    [getFilteredData, pageSize, status, startDate, endDate, telRef, emailRef, nameRef]
  );

  // 빠른 날짜 선택
  const handleQuickDate = (type: 'all' | 'today' | '30days') => {
    const today = new Date();
    if (type === 'all') {
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(today.getFullYear() - 1);
      setStartDate(oneYearAgo);
      setEndDate(today);
    } else if (type === 'today') {
      setStartDate(today);
      setEndDate(today);
    } else if (type === '30days') {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(today.getDate() - 30);
      setStartDate(thirtyDaysAgo);
      setEndDate(today);
    }
  };

  // 초기화
  const handleReset = () => {
    if (telRef.current) telRef.current.value = '';
    if (emailRef.current) emailRef.current.value = '';
    if (nameRef.current) nameRef.current.value = '';
    setStatus('');
    const today = new Date();
    const twoMonthsAgo = new Date();
    twoMonthsAgo.setMonth(today.getMonth() - 2);
    setStartDate(twoMonthsAgo);
    setEndDate(today);
  };

  // 모바일 검색 폼 콘텐츠
  const mobileSearchContent = (
    <form onSubmit={handleSearch} className="!p-3 !space-y-2">
      {/* 1행: 상태 + 페이지 사이즈 */}
      <div className="!grid !grid-cols-2 !gap-2">
        <Select
          options={[
            { option: '전체 현황', value: '' },
            ...contractStatus.map(s => ({
              option: s.description,
              value: s.code,
            })),
          ]}
          onChange={onChangeContractStatus}
          value={status}
          style={{ width: '100%' }}
        />
        <Select
          options={[
            { option: '20개씩', value: '20' },
            { option: '50개씩', value: '50' },
            { option: '100개씩', value: '100' },
          ]}
          onChange={onChangePageSize}
          value={pageSize}
          style={{ width: '100%' }}
        />
      </div>

      {/* 2행: 날짜 */}
      <div className="!flex !items-center !gap-1">
        <div className="!flex-1">
          <DatePickerInput
            value={startDate}
            onChange={handleStartDateChange}
            placeholder="시작"
            width="100%"
          />
        </div>
        <span className="!text-gray-400 !text-xs">~</span>
        <div className="!flex-1">
          <DatePickerInput
            value={endDate}
            onChange={handleEndDateChange}
            placeholder="종료"
            width="100%"
          />
        </div>
      </div>

      {/* 3행: 검색어 입력 */}
      <div className="!flex !gap-1">
        <Input inputRef={telRef} placeholder="전화번호" textAlign="left" width="38%" />
        <Input inputRef={emailRef} placeholder="이메일" textAlign="left" width="42%" />
        <Input inputRef={nameRef} placeholder="이름" textAlign="left" width="20%" />
      </div>

      {/* 하단: 초기화 + 검색 */}
      <div className="!grid !grid-cols-2 !gap-2 !pt-1">
        <button
          type="button"
          onClick={handleReset}
          className="!py-2 !text-sm !border !border-gray-300 !rounded !bg-white hover:!bg-gray-50"
        >
          초기화
        </button>
        <button
          type="submit"
          className="!py-2 !text-sm !text-white !bg-blue-500 !rounded hover:!bg-blue-600"
        >
          검색
        </button>
      </div>
    </form>
  );

  return (
    <div className="!w-full">
      {/* 모바일: MobileFilterTabs 사용 */}
      <MobileFilterTabs
        tabs={[{ id: 'search', label: '검색', content: mobileSearchContent }]}
        showFranchiseTab={isAdmin && !!setSelectedFranchiseId}
        selectedFranchiseId={selectedFranchiseId}
        onFranchiseSelect={(id) => {
          setSelectedFranchiseId?.(id);
          getFilteredData({ franchiseId: id });
        }}
      />

      {/* 데스크톱 검색 필터 (기존) */}
      <form
        onSubmit={handleSearch}
        className="!hidden md:!flex !flex-row !items-center !justify-between !w-full !gap-4"
      >
        <div className="!flex !items-center !gap-3 !flex-wrap">
          <Select
            options={[
              { option: '주문 현황', value: '' },
              ...contractStatus.map(s => ({
                option: s.description,
                value: s.code,
              })),
            ]}
            onChange={onChangeContractStatus}
            value={status}
            style={{ width: '150px' }}
          />
          <div className="!flex !items-center !gap-1">
            <DatePickerInput
              value={startDate}
              onChange={handleStartDateChange}
              placeholder="시작일"
              width={130}
            />
            <span className="!text-gray-400">~</span>
            <DatePickerInput
              value={endDate}
              onChange={handleEndDateChange}
              placeholder="종료일"
              width={130}
            />
          </div>
          <Input inputRef={telRef} placeholder="전화번호" textAlign="left" width={140} />
          <Input inputRef={emailRef} placeholder="이메일" textAlign="left" width={200} />
          <div className="!relative">
            <Input
              inputRef={nameRef}
              placeholder="이름"
              textAlign="left"
              width={160}
              style={{ paddingRight: '40px' }}
            />
            <button
              type="submit"
              className="!absolute !right-1 !top-1/2 !-translate-y-1/2 !w-8 !h-8 !bg-blue-400 hover:!bg-blue-500 !rounded !flex !items-center !justify-center !transition-colors !border-0"
            >
              <FontAwesomeIcon icon={faMagnifyingGlass} className="!text-white !text-sm" />
            </button>
          </div>
        </div>
        <Select
          options={[
            { option: '20개씩', value: '20' },
            { option: '50개씩', value: '50' },
            { option: '100개씩', value: '100' },
            { option: '300개씩', value: '300' },
          ]}
          onChange={onChangePageSize}
          value={pageSize}
          style={{ width: '120px' }}
        />
      </form>
    </div>
  );
};

export default OrderFixFilter;
